import { createSelector } from '@reduxjs/toolkit';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { orderBy } from 'lodash';
import {
  Account,
  CreditAlert,
  CreditInquiry,
  CreditRecommendationType,
  CreditReport,
  CreditSummary,
  PublicRecord,
  ScoreDetails,
} from 'mcsclient/models';

import { CreditAlertRecommendation } from './creditSummary.types';
import { getITPFamilyMemberFromCsid } from './identityMonitoringSelectors';
import { IIdentityMonitoringState } from './identityMonitoringState';

export type WithIndex<T> = T & { index: number };

export interface IndexedCreditReport extends CreditReport {
  accounts?: WithIndex<Account>[];
  creditInquiries?: WithIndex<CreditInquiry>[];
  publicRecords?: WithIndex<PublicRecord>[];
}

/**
 * Select and sort accounts from credit summary and add an index to each account
 *
 * @param creditSummary
 */
const selectCreditReportAccounts: (creditSummary: CreditSummary | undefined) => WithIndex<Account>[] = createSelector(
  [(creditSummary) => creditSummary?.report?.accounts],
  (accounts) =>
    orderBy(
      accounts,
      [(account) => account.statusDate?.getTime(), (account) => account.accountName, (account) => account.accountNumber],
      ['desc', 'asc', 'asc']
    ).map((account, index) => ({
      ...account,
      index,
    }))
);

/**
 * Select and sort credit inquiries from credit summary and add an index to each credit inquiry
 *
 * @param creditSummary
 */
const selectCreditReportInquiries: (creditSummary: CreditSummary | undefined) => WithIndex<CreditInquiry>[] = createSelector(
  [(creditSummary) => creditSummary?.report?.creditInquiries],
  (inquiries) =>
    orderBy(inquiries, [(inquiry) => inquiry.inquiryDateTime?.getTime(), (inquiry) => inquiry.companyName], ['desc', 'asc']).map(
      (inquiry, index) => ({
        ...inquiry,
        index,
      })
    )
);

/**
 * Select and sort public records from credit summary and add an index to each public record
 *
 * @param creditSummary
 */
const selectCreditReportPublicRecords: (creditSummary: CreditSummary | undefined) => WithIndex<PublicRecord>[] = createSelector(
  [(creditSummary) => creditSummary?.report?.publicRecords],
  (records) =>
    orderBy(records, [(record) => record.filedDate?.getTime(), (record) => record.filedBy], ['desc', 'asc']).map((record, index) => ({
      ...record,
      index,
    }))
);

/**
 * Select credit report from state for the specified csid, and sort and add an indices to the accounts, credit inquiries and public records
 *
 * @param state
 * @param csid
 */
export const selectCreditReport: (state: IIdentityMonitoringState, csid: string | undefined) => IndexedCreditReport = createSelector(
  [getITPFamilyMemberFromCsid],
  (member) => ({
    ...member?.creditSummary?.report,
    accounts: selectCreditReportAccounts(member?.creditSummary),
    creditInquiries: selectCreditReportInquiries(member?.creditSummary),
    publicRecords: selectCreditReportPublicRecords(member?.creditSummary),
  })
);

/**
 * Select credit score details from state for the specified csid
 *
 * @param state
 * @param csid
 */
export const selectCreditScore: (state: IIdentityMonitoringState, csid: string | undefined) => ScoreDetails = createSelector(
  [getITPFamilyMemberFromCsid],
  (member) => ({
    ...member?.creditSummary?.score,
  })
);

/**
 * Select credit alerts from state for the specified csid
 *
 * @param state
 * @param csid
 */
const getCreditSummaryAlerts: (state: IIdentityMonitoringState, csid?: string) => CreditAlert[] = createSelector(
  [getITPFamilyMemberFromCsid],
  (member) => member?.creditSummary?.alerts ?? []
);

/**
 * Select credit alert recommendations from state for the specified csid and alertId
 *
 * @param state
 * @param csid
 * @param alertId
 */
const getCreditSummaryAlertRecommendations: (
  state: IIdentityMonitoringState,
  csid: string | undefined,
  alertId: string
) => CreditAlertRecommendation[] = createSelector(
  [getCreditSummaryAlerts, (state: IIdentityMonitoringState, csid: string | undefined, alertId: string) => alertId],
  (alerts, alertId) => alerts.find((alert) => localeIgnoreCaseEquals(alert.id, alertId))?.recommendations ?? []
);

/**
 * Select credit alert recommendation from state for the specified csid, alertId and recommendationType
 *
 * @param state
 * @param csid
 * @param alertId
 * @param recommendationType
 */
export const getCreditRecommendationByType: (
  state: IIdentityMonitoringState,
  csid: string | undefined,
  alertId: string,
  recommendationType: CreditRecommendationType
) => CreditAlertRecommendation | undefined = createSelector(
  [
    getCreditSummaryAlertRecommendations,
    (state: IIdentityMonitoringState, csid: string | undefined, alertId: string, recommendationType: CreditRecommendationType) =>
      recommendationType,
  ],
  (recommendations, recommendationType) => recommendations.find((r) => r.recommendationType === recommendationType)
);
